// Opt-In Evidence Page
// Public page showing SMS consent flow for Twilio A2P 10DLC compliance verification

import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, ArrowLeft, CheckSquare, Phone, Shield, FileText } from 'lucide-react';

const OptInEvidencePage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              IntakePal
            </span>
          </Link>
          <Link 
            to="/" 
            className="text-sm text-slate-600 hover:text-teal-600 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                SMS Opt-In Evidence
              </h1>
              <p className="text-slate-500 text-sm">Documentation for A2P 10DLC Campaign Verification</p>
            </div>
          </div>

          {/* Introduction */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <p className="text-blue-800">
              This page documents how IntakePal collects SMS consent from end users through our embeddable chat widget. 
              This information is provided for carrier and campaign registry review purposes.
            </p>
          </div>

          {/* Section 1: Overview */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              Opt-In Method Overview
            </h2>
            <div className="bg-slate-50 rounded-xl p-6">
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-3">
                  <CheckSquare className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Method:</strong> Website form opt-in via SMS signup page or embedded chat widget</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckSquare className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Location:</strong> https://intakepal.com/sms-signup and law firm websites</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckSquare className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Consent Type:</strong> OPTIONAL checkbox consent (unchecked by default, not required to proceed)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckSquare className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Message Frequency:</strong> Up to 4 automated messages per inquiry</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckSquare className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Important:</strong> Users can submit their information WITHOUT opting into SMS. SMS consent is optional.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 2: Visual Evidence */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              Opt-In Flow Screenshots
            </h2>

            {/* Screenshot 1: Widget Button */}
            <div className="mb-6">
              <h3 className="font-medium text-slate-800 mb-3">Step 1: Chat Widget on Law Firm Website</h3>
              <div className="bg-slate-100 rounded-xl p-6 border-2 border-dashed border-slate-300">
                <div className="flex items-end justify-end">
                  <div className="flex flex-col items-center gap-2">
                    <div className="bg-slate-900 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg relative">
                      Injured? Get Help Now - It&apos;s Free
                      <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-slate-900"></div>
                    </div>
                    <div className="relative">
                      <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full text-white text-xs font-bold flex items-center justify-center border-[3px] border-white shadow-lg z-10">1</span>
                      <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center shadow-lg">
                        <MessageSquare className="w-8 h-8 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-4 text-center">
                  The IntakePal chat widget appears as a floating button on law firm websites
                </p>
              </div>
            </div>

            {/* Screenshot 2: Phone Input with Consent */}
            <div className="mb-6">
              <h3 className="font-medium text-slate-800 mb-3">Step 2: Phone Number Collection with SMS Consent Checkbox</h3>
              <div className="bg-slate-100 rounded-xl p-6 border-2 border-dashed border-slate-300">
                <div className="max-w-sm mx-auto">
                  {/* Mock Chat Window */}
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
                    {/* Header */}
                    <div className="bg-slate-900 px-4 py-3 flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Smith & Associates Law</p>
                        <p className="text-slate-400 text-xs">Legal Intake Assistant</p>
                      </div>
                    </div>
                    
                    {/* Chat Content */}
                    <div className="p-4 bg-slate-50">
                      {/* Bot Message */}
                      <div className="flex gap-2 mb-4">
                        <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="w-4 h-4 text-white" />
                        </div>
                        <div className="bg-white rounded-2xl rounded-tl-md px-4 py-2 shadow-sm border border-slate-100">
                          <p className="text-slate-700 text-sm">And your phone number?</p>
                        </div>
                      </div>
                    </div>

                    {/* Input Area with Consent */}
                    <div className="p-4 border-t border-slate-200 bg-white">
                      <div className="flex gap-2 mb-3">
                        <input 
                          type="tel" 
                          placeholder="(555) 123-4567" 
                          className="flex-1 px-4 py-3 border border-slate-200 rounded-lg text-sm bg-white"
                          readOnly
                        />
                        <button className="w-11 h-11 bg-teal-500 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22,2 15,22 11,13 2,9"></polygon>
                          </svg>
                        </button>
                      </div>
                      
                      {/* CONSENT CHECKBOX - THE KEY ELEMENT */}
                      <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input 
                            type="checkbox" 
                            className="w-5 h-5 mt-0.5 accent-teal-500 flex-shrink-0"
                          />
                          <span className="text-sm text-slate-700 leading-relaxed">
                            I agree to receive text messages about my case. Message &amp; data rates may apply. Reply STOP to opt out.
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-4 text-center">
                  Users must check the consent checkbox to receive SMS messages. The checkbox is <strong>unchecked by default</strong>.
                </p>
              </div>
            </div>

            {/* Screenshot 3: Consent Text Close-up */}
            <div className="mb-6">
              <h3 className="font-medium text-slate-800 mb-3">Step 3: Consent Language (Close-up)</h3>
              <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 border-2 border-slate-400 rounded bg-white flex items-center justify-center flex-shrink-0">
                    <CheckSquare className="w-5 h-5 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-lg text-slate-800 font-medium">
                      &quot;I agree to receive text messages about my case. Message &amp; data rates may apply. Reply STOP to opt out.&quot;
                    </p>
                    <p className="text-sm text-slate-500 mt-2">
                      This is the exact consent language displayed to users. The checkbox must be manually checked by the user.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Consent Details */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              Consent Collection Details
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <tbody>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 pr-4 font-medium text-slate-700 w-1/3">Consent Mechanism</td>
                    <td className="py-3 text-slate-600">Checkbox (unchecked by default)</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 pr-4 font-medium text-slate-700">Consent Language</td>
                    <td className="py-3 text-slate-600">&quot;I agree to receive text messages about my case. Message &amp; data rates may apply. Reply STOP to opt out.&quot;</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 pr-4 font-medium text-slate-700">Message Frequency</td>
                    <td className="py-3 text-slate-600">Up to 4 automated messages per inquiry</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 pr-4 font-medium text-slate-700">Opt-Out Method</td>
                    <td className="py-3 text-slate-600">Reply STOP to any message</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 pr-4 font-medium text-slate-700">Data Recorded</td>
                    <td className="py-3 text-slate-600">Phone number, consent status (true/false), timestamp, source URL</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 font-medium text-slate-700">Who Receives SMS</td>
                    <td className="py-3 text-slate-600">Only users who explicitly check the consent checkbox</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 4: Legal Documents */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
              Required Legal Documents
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <a 
                href="/privacy" 
                target="_blank"
                className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-teal-300 hover:bg-teal-50 transition-colors"
              >
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Privacy Policy</p>
                  <p className="text-sm text-teal-600">https://intakepal.com/privacy</p>
                </div>
              </a>
              <a 
                href="/terms" 
                target="_blank"
                className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-teal-300 hover:bg-teal-50 transition-colors"
              >
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Terms of Service</p>
                  <p className="text-sm text-teal-600">https://intakepal.com/terms</p>
                </div>
              </a>
            </div>
          </section>

          {/* Section 5: Privacy Policy Compliance */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
              Privacy Policy SMS Disclosures
            </h2>
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-6">
              <p className="text-teal-800 mb-4">
                Our Privacy Policy (https://intakepal.com/privacy) includes the following required disclosures:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckSquare className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-teal-800"><strong>Non-sharing statement:</strong> &quot;We do not share, sell, or transfer your mobile phone number to third parties or affiliates for marketing or promotional purposes.&quot;</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckSquare className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-teal-800"><strong>Message frequency:</strong> &quot;You will receive up to 4 automated messages per inquiry.&quot;</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckSquare className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-teal-800"><strong>Rates disclosure:</strong> &quot;Message and data rates may apply.&quot;</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckSquare className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-teal-800"><strong>Opt-out instructions:</strong> &quot;Reply STOP to any SMS message to unsubscribe at any time.&quot;</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 6: Sample Messages */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">6</span>
              Sample SMS Messages
            </h2>
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs text-slate-500 mb-2">Initial Confirmation</p>
                <p className="text-slate-700 font-mono text-sm bg-white p-3 rounded-lg border border-slate-200">
                  Smith & Associates Law: Thank you for your car accident inquiry. An attorney will review shortly. Msg frequency: up to 4 msgs. Msg & data rates may apply. Reply STOP to opt out.
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs text-slate-500 mb-2">Follow-up Message</p>
                <p className="text-slate-700 font-mono text-sm bg-white p-3 rounded-lg border border-slate-200">
                  Smith & Associates Law: Following up on your injury case. Call us at (555) 123-4567 to discuss. Msg & data rates may apply. Reply STOP to opt out, HELP for help.
                </p>
              </div>
            </div>
          </section>

          {/* Footer Note */}
          <div className="mt-10 pt-6 border-t border-slate-200">
            <p className="text-sm text-slate-500 text-center">
              This documentation is provided for A2P 10DLC campaign registration and carrier compliance verification.
              <br />
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OptInEvidencePage;
